import { useMutation } from '@apollo/client'
import { Grid, Typography } from '@mui/material'
import moment from 'moment'
import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SEND_CUSTOMER_INVOICE_MUTATION, SEND_INVOICE_MUTATION } from '../../../graphql/mutations'
import { useCase, useNotification } from '../../../utils/hooks'
import { Button, Input } from '../../atoms'
import FinanceStatusBadge from '../../atoms/FinanceStatusBadge/FinanceStatusBadge'
import DownloadButton from './DownloadButton'
import { IProps } from './interfaces'

const BillingButton: React.FunctionComponent<IProps> = ({
  label,
  disabled,
  mutation,
  helperText,
  value,
  queryOptions,
  options,
  callback
}) => {
  const { t } = useTranslation(['common', 'billing'])
  const { setNotification } = useNotification()

  const methods = useFormContext()
  const { getValues } = methods
  const { processing } = useCase()
  const [load, setLoad] = useState(false)
  const getMutation = () => {
    if (options?.type === 'customer') {
      return SEND_CUSTOMER_INVOICE_MUTATION
    }
    return SEND_INVOICE_MUTATION
  }
  const [invoiceMutation] = useMutation(mutation || getMutation(), {
    ...queryOptions,
    variables: { ...(queryOptions?.variables || {}) },
    onCompleted: () => {
      setNotification({
        open: true,
        variant: 'success',
        title: t('success'),
        message: t('billing:success')
      })
      if (callback) callback()
    },
    onError: error => {
      setNotification({ open: true, variant: 'error', title: t('error'), message: error?.message })
    }
  })

  const renderHelperText = () => {
    const messages = []
    if (helperText) messages.push(helperText)
    return messages.length ? (
      <Typography
        variant="caption"
        sx={{
          paddingLeft: '16px',
          paddingTop: '5px',
          whiteSpace: 'pre-wrap',
          color: theme => theme.palette.text.secondary
        }}
      >
        {messages.join(' \n')}
      </Typography>
    ) : null
  }

  const intern: boolean | undefined = processing?.assignedProcessingType?.intern
  const getAdditionalAttributes = () => {
    let attributes = {}
    if (options?.type === 'customer') {
      const values = getValues() || {}
      const {
        processingsAttributes: {
          [options?.id]: {
            customerInvoice: {
              dataset: {
                customer: {
                  salutation,
                  lastname,
                  firstname,
                  birthDate,
                  company,
                  street,
                  houseNumber,
                  postalCode,
                  place,
                  country,
                  email
                }
              }
            }
          }
        } = {}
      } = values

      attributes = {
        salutation,
        lastname,
        firstname,
        birthDate,
        company,
        street,
        houseNumber,
        postalCode,
        place,
        country,
        email
      }
    }
    return attributes
  }
  return (
    <Grid container spacing={2}>
      {value?.invoiceNo && (
        <Grid size={{ xs: 12 }}>
          <Input
            label="Rechnungsnummer"
            onChange={() => {}}
            defaultValue={value.invoiceNo}
            disabled
            fullWidth
          />
        </Grid>
      )}
      <Grid size={{ xs: 12 }}>
        {value?.financeStatus && !value?.documentUrl ? (
          // Cutover (docs/35 K3): Rechnung ist unterwegs in m2-finance —
          // Badge statt Send-Button (Doppel-Push-Schutz).
          <FinanceStatusBadge status={value.financeStatus} />
        ) : value?.documentUrl ? (
          <DownloadButton options={{ url: value.documentUrl }} label={label} />
        ) : (
          <>
            {intern ? (
              <Typography
                variant="caption"
                sx={{ display: 'flex', width: '100%', paddingTop: '5px' }}
              >
                {t('billing:onlyInternAssignedProcessingType')}
              </Typography>
            ) : (
              <Button
                loading={load}
                loadingPosition="start"
                fullWidth
                onClick={async () => {
                  const executeAction = async () => {
                    setLoad(true)
                    await invoiceMutation(
                      queryOptions?.variables
                        ? {
                            variables: {
                              ...queryOptions.variables,
                              ...(options?.type === 'customer'
                                ? { attributes: getAdditionalAttributes() }
                                : {})
                            }
                          }
                        : {}
                    )
                    setLoad(false)
                  }

                  if (options?.onBeforeClick) {
                    options.onBeforeClick(executeAction)
                  } else {
                    await executeAction()
                  }
                }}
                disabled={disabled || load}
              >
                <>{label}</>
              </Button>
            )}
          </>
        )}
      </Grid>

      {renderHelperText()}

      <Typography
        variant="caption"
        sx={{ display: 'flex', width: '100%', justifyContent: 'center', paddingTop: '5px' }}
      >
        {value?.sentAt
          ? `Rechnung versendet: ${moment(value?.sentAt).format('DD.MM.YYYY HH:mm')}`
          : ''}
      </Typography>
    </Grid>
  )
}

export default BillingButton
